import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { FileText, Truck, Clock, CheckCircle2, XCircle, Pencil, FileSpreadsheet, Upload, Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TermsTemplatePicker } from "@/components/TermsTemplatePicker";
import { useActiveTermsTemplates } from "@/hooks/useActiveTermsTemplates";
import { buildTermsSnapshot, splitTermsSnapshot } from "@/lib/termsSnapshot";
import { toast } from "sonner";
import { exportPurchaseOrdersExcel } from "@/lib/procurementExports";
import { PoCommActions } from "@/pages/AutoProcurement";
import PurchaseOrderEditor, { draftFromPreview } from "@/components/PurchaseOrderEditor";
import { formatMoney, docCurrency, totalsByCurrency, formatCurrencyTotals } from "@/lib/currency";

const ACK_TONE = {
  pending: "bg-amber-50 text-amber-700",
  confirmed: "bg-emerald-50 text-emerald-700",
  declined: "bg-red-50 text-red-700",
};

const STATUS_LABEL = {
  open: { cls: "bg-amber-50 text-amber-700", Icon: Clock, label: "Open" },
  received: { cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2, label: "Received" },
  cancelled: { cls: "bg-red-50 text-red-700", Icon: XCircle, label: "Cancelled" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 86400000);
}

// Local shape of the T&C edit form kept inside the modal, seeded from a PO's
// saved terms_ids/terms_snapshot via splitTermsSnapshot() and combined back
// into a single terms_snapshot via buildTermsSnapshot() on save — the same
// shared helpers Raise Transfer Order and Quotations use.
const emptyTermsForm = () => ({
  terms_ids: [],
  terms_overrides: {},
  terms_default_override: undefined,
  terms_extra_text: "",
});

export default function PurchaseOrdersPage() {
  const [pos, setPos] = useState([]);
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState(null);
  const { templates: termsTemplates, loading: termsLoading } = useActiveTermsTemplates();

  // T&C edit modal state
  const [termsPo, setTermsPo] = useState(null); // the PO currently being edited, or null when closed
  const [termsForm, setTermsForm] = useState(emptyTermsForm());
  const [savingTerms, setSavingTerms] = useState(false);

  // New Purchase Order / Import PO (backend/po_import.py)
  const [suppliers, setSuppliers] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [importQueue, setImportQueue] = useState([]); // drafts from an import, reviewed one at a time
  const [importTotal, setImportTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [p, i, cs, sp] = await Promise.all([
      api.get("/purchase-orders"),
      api.get("/items"),
      api.get("/settings/company").catch(() => ({ data: null })),
      api.get("/suppliers").catch(() => ({ data: [] })),
    ]);
    setPos(p.data);
    setItems(i.data);
    setCompany(cs.data || null);
    setSuppliers(sp.data || []);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/purchase-orders/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const drafts = (data.drafts || []).map(draftFromPreview);
      if (!drafts.length) { toast.error("No purchase order found in the file"); return; }
      setImportQueue(drafts);
      setImportTotal(drafts.length);
      setEditorOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  const nextImport = () => {
    setImportQueue((q) => {
      const rest = q.slice(1);
      if (!rest.length) setEditorOpen(false);
      return rest;
    });
  };
  const onEditorClose = () => (importQueue.length ? nextImport() : setEditorOpen(false));
  const onEditorCreated = async () => {
    if (importQueue.length) nextImport(); else setEditorOpen(false);
    await load();
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/purchase-orders/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "purchase_order_import_template.xlsx"; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  useEffect(() => { load(); }, []);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const summary = useMemo(() => {
    const open = pos.filter((p) => p.status === "open");
    return {
      open: open.length,
      received: pos.filter((p) => p.status === "received").length,
      cancelled: pos.filter((p) => p.status === "cancelled").length,
      // Per currency — INR and USD POs are never added together.
      open_value: totalsByCurrency(open, (p) => p.total_cost),
    };
  }, [pos]);

  const exportList = () => {
    try {
      exportPurchaseOrdersExcel({ pos, itemMap });
      toast.success(`Exported ${pos.length} purchase order(s)`);
    } catch {
      toast.error("Export failed");
    }
  };

  const openTermsModal = (po) => {
    const { overrides, defaultOverride, extraText } = splitTermsSnapshot(po.terms_snapshot);
    setTermsForm({
      terms_ids: Array.isArray(po.terms_ids) && po.terms_ids.length ? po.terms_ids : (po.terms_id ? [po.terms_id] : []),
      terms_overrides: overrides,
      terms_default_override: defaultOverride,
      terms_extra_text: extraText,
    });
    setTermsPo(po);
  };

  const closeTermsModal = () => {
    setTermsPo(null);
    setTermsForm(emptyTermsForm());
  };

  const saveTerms = async () => {
    if (!termsPo) return;
    setSavingTerms(true);
    try {
      const snapshot = buildTermsSnapshot({
        templates: termsTemplates,
        selectedIds: termsForm.terms_ids,
        overrides: termsForm.terms_overrides,
        defaultText: company?.default_terms,
        defaultOverride: termsForm.terms_default_override,
        extraText: termsForm.terms_extra_text,
      });
      await api.put(`/purchase-orders/${termsPo.id}/terms`, {
        terms_ids: termsForm.terms_ids || [],
        terms_snapshot: snapshot,
      });
      toast.success(`Terms & Conditions updated for ${termsPo.code}`);
      closeTermsModal();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update Terms & Conditions");
    } finally {
      setSavingTerms(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="po-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Procurement</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Purchase Orders
          </h1>
          <p className="text-sm text-slate-500 mt-1">Generated from approved Purchase Requests on order, entered here, or imported from a Tally PO (PDF / Excel).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} data-testid="po-import-template-btn" title="Excel template for importing many POs at once">
            <Download className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={exportList} disabled={pos.length === 0} data-testid="po-export-list-btn"
            title="Export all purchase orders to Excel">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="po-import-btn"
            title="Import a Tally Purchase Order PDF, the same voucher exported to Excel, or the PO template (.xlsx / .csv)">
            <Upload className="w-4 h-4 mr-1.5" /> {importing ? "Reading…" : "Import PO"}
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xlsm,.csv" className="hidden" onChange={onImportFile} data-testid="po-import-input" />
          <Button onClick={() => { setImportQueue([]); setEditorOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="po-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Purchase Order
          </Button>
        </div>
      </div>

      <PurchaseOrderEditor
        open={editorOpen}
        initial={importQueue[0] || null}
        stepLabel={importTotal > 1 && importQueue.length ? `PO ${importTotal - importQueue.length + 1} of ${importTotal}` : ""}
        items={items}
        suppliers={suppliers}
        onClose={onEditorClose}
        onCreated={onEditorCreated}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card" data-testid="po-kpi-open">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Open POs</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary.open}</div>
        </div>
        <div className="kpi-card" data-testid="po-kpi-value">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Open Value</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900 mono" style={{ fontFamily: "Manrope" }}>{formatCurrencyTotals(summary.open_value)}</div>
        </div>
        <div className="kpi-card" data-testid="po-kpi-received">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Received</div>
          <div className="mt-2 text-3xl font-extrabold text-emerald-700" style={{ fontFamily: "Manrope" }}>{summary.received}</div>
        </div>
        <div className="kpi-card" data-testid="po-kpi-cancelled">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Cancelled</div>
          <div className="mt-2 text-3xl font-extrabold text-red-700" style={{ fontFamily: "Manrope" }}>{summary.cancelled}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="po-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">PO</th>
              <th className="py-3 px-4">From PR</th>
              <th className="py-3 px-4">Supplier</th>
              <th className="py-3 px-4">Lines</th>
              <th className="py-3 px-4">Total</th>
              <th className="py-3 px-4">Expected</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Terms &amp; Conditions</th>
              <th className="py-3 px-4">Supplier Confirmation</th>
              <th className="py-3 px-4 text-right">Send / PDF</th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-sm text-slate-500" data-testid="no-pos">
                No purchase orders yet. Click New Purchase Order, import a Tally PO, or approve a PR and mark it Ordered.
              </td></tr>
            )}
            {pos.map((po) => {
              const meta = STATUS_LABEL[po.status] || STATUS_LABEL.open;
              const days = daysUntil(po.expected_receipt_date);
              const termsCount = Array.isArray(po.terms_ids) ? po.terms_ids.length : (po.terms_id ? 1 : 0);
              return (
                <tr key={po.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`po-row-${po.code}`}>
                  <td className="py-3 px-4 mono font-semibold text-slate-800">
                    {po.code}
                    {(po.source === "import" || po.source === "manual") && (
                      <div className="text-[10px] font-normal text-slate-500 normal-case" data-testid={`po-source-${po.code}`}>
                        {po.source === "import" ? "Imported" : "Manual"}{po.external_po_no ? ` · Supplier PO ${po.external_po_no}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 mono text-blue-700">{po.pr_code}</td>
                  <td className="py-3 px-4 text-slate-700">
                    <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-slate-400" />{po.supplier || "Unassigned"}</span>
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {(po.lines || []).map((l, i) => (
                      <span key={i} className="inline-block mr-2 mono text-xs">
                        {itemMap[l.item_id]?.code || "?"}×{l.qty}
                      </span>
                    ))}
                  </td>
                  <td className="py-3 px-4 mono font-semibold">{formatMoney(po.total_cost, docCurrency(po))}</td>
                  <td className="py-3 px-4 text-slate-700">
                    <div className="text-sm">{po.expected_receipt_date ? new Date(po.expected_receipt_date).toLocaleDateString() : "—"}</div>
                    {po.status === "open" && days !== null && (
                      <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${days < 0 ? "text-red-600 font-semibold" : days <= 3 ? "text-amber-600" : "text-slate-500"}`}>
                        {days < 0 ? `${-days}d overdue` : days === 0 ? "Today" : `in ${days}d`}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openTermsModal(po)}
                      data-testid={`po-edit-terms-${po.code}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      {termsCount > 0 ? `${termsCount} template${termsCount > 1 ? "s" : ""}` : "Add T&C"}
                    </Button>
                  </td>
                  <td className="py-3 px-4" data-testid={`po-ack-${po.code}`}>
                    {po.dispatched_at ? (
                      <>
                        <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${ACK_TONE[po.ack_status] || ACK_TONE.pending}`}>
                          {po.ack_status || "pending"}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {po.email_status === "sent" || ["sent", "sent_manually"].includes(po.whatsapp_status) ? "Sent" : "Not delivered · tried"}{" "}
                          {new Date(po.dispatched_at).toLocaleDateString()}
                          {po.followup_count ? ` · ${po.followup_count} follow-up(s)` : ""}
                          {po.auto_generated ? " · Auto MRP" : ""}
                        </div>
                        <div
                          className="text-[10px] mt-0.5"
                          title={(po.dispatch_errors || []).join("\n") || undefined}
                          data-testid={`po-channels-${po.code}`}
                        >
                          <span className={po.email_status === "sent" ? "text-emerald-600" : "text-red-600"}>
                            Email: {(po.email_status || "—").replace(/_/g, " ")}
                          </span>
                          {" · "}
                          {["sent", "sent_manually"].includes(po.whatsapp_status) ? (
                            <span className="text-emerald-600">WhatsApp: {po.whatsapp_status === "sent" ? "sent" : "shared"}</span>
                          ) : ["disabled", "use_share_button", "not_sent", undefined, null].includes(po.whatsapp_status) ? (
                            <span className="text-slate-500">WhatsApp: not shared yet</span>
                          ) : (
                            <span className="text-red-600">WhatsApp: {po.whatsapp_status.replace(/_/g, " ")}</span>
                          )}
                        </div>
                      </>
                    ) : <span className="text-xs text-slate-400">Not sent</span>}
                  </td>
                  <td className="py-3 px-4">
                    <PoCommActions po={po} onDone={load} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-1">
        <FileText className="w-3.5 h-3.5" />
        POs are auto-closed (status = received) when the source PR is marked Received.
      </div>

      {/* Terms & Conditions edit modal — same shared picker + snapshot
          combine logic as Raise Transfer Order and Quotations. POs are
          auto-generated from Purchase Requests, so T&C is attached here
          after the fact rather than at creation time. */}
      <Dialog open={!!termsPo} onOpenChange={(open) => !open && closeTermsModal()}>
        <DialogContent className="max-w-lg" data-testid="po-terms-dialog">
          <DialogHeader>
            <DialogTitle>Terms &amp; Conditions — {termsPo?.code}</DialogTitle>
          </DialogHeader>
          <TermsTemplatePicker
            templates={termsTemplates}
            loading={termsLoading}
            selectedIds={termsForm.terms_ids}
            onSelectedIdsChange={(ids) => setTermsForm((f) => ({ ...f, terms_ids: ids }))}
            overrides={termsForm.terms_overrides}
            onOverridesChange={(overrides) => setTermsForm((f) => ({ ...f, terms_overrides: overrides }))}
            defaultText={company?.default_terms}
            defaultOverride={termsForm.terms_default_override}
            onDefaultOverrideChange={(v) => setTermsForm((f) => ({ ...f, terms_default_override: v }))}
            extraText={termsForm.terms_extra_text}
            onExtraTextChange={(v) => setTermsForm((f) => ({ ...f, terms_extra_text: v }))}
            docLabel="purchase order"
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeTermsModal} data-testid="po-terms-cancel">Cancel</Button>
            <Button onClick={saveTerms} disabled={savingTerms} className="bg-blue-600 hover:bg-blue-700" data-testid="po-terms-save">
              {savingTerms ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
